import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Pencil } from 'lucide-react';
import { addParticipant, updateParticipant } from '../../api/forumAPI';
import { useNotification } from '../../../../context/NotificationContext';
import CustomSelect from '../../../../components/ui/CustomSelect';

export default function AddParticipantModal({ isOpen, onClose, dossierId, participantToEdit, onSuccess }) {
  const { showToast } = useNotification();
  const [formData, setFormData] = useState({
    role: 'Avocat',
    name: '',
    contactInfo: ''
  });
  const [loading, setLoading] = useState(false);

  const roles = ['Avocat', 'Notaire', 'Banquier', 'Expert-Comptable', 'Consultant', 'Autre'];

  useEffect(() => {
    if (isOpen) {
      if (participantToEdit) {
        setFormData({
          role: participantToEdit.role || 'Avocat',
          name: participantToEdit.name || '',
          contactInfo: participantToEdit.contactNotes || ''
        });
      } else {
        setFormData({ role: 'Avocat', name: '', contactInfo: '' });
      }
    }
  }, [isOpen, participantToEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.role) return;

    setLoading(true);
    try {
      if (participantToEdit) {
        await updateParticipant(dossierId, participantToEdit.id, formData);
        showToast("Intervenant modifié avec succès", "success");
      } else {
        await addParticipant(dossierId, formData);
        showToast("Intervenant ajouté avec succès", "success");
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du participant", error);
      showToast("Erreur lors de l'enregistrement", "error");
    } finally {
      setLoading(false);
    }
  };

  const isEditing = !!participantToEdit;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="relative bg-surface rounded-xl shadow-hp-xl border border-workspace w-full max-w-md overflow-hidden flex flex-col z-10"
          >
            <div className="flex justify-between items-center p-6 border-b border-workspace bg-surface-muted">
              <h3 className="text-lg font-bold text-primary flex items-center">
                {isEditing ? (
                  <Pencil className="w-5 h-5 mr-2 text-tertiary" />
                ) : (
                  <UserPlus className="w-5 h-5 mr-2 text-tertiary" />
                )}
                {isEditing ? 'Modifier l\'intervenant' : 'Ajouter un intervenant'}
              </h3>
              <button onClick={onClose} className="text-tertiary hover:text-primary transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-primary mb-1">Rôle *</label>
                <CustomSelect
                  value={formData.role}
                  onChange={(val) => setFormData(prev => ({ ...prev, role: val }))}
                  options={roles.map(r => ({ label: r, value: r }))}
                  placeholder="Sélectionner un rôle..."
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary mb-1">Nom / Structure *</label>
                <input
                  type="text"
                  className="workspace-input w-full"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Me Dupont"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary mb-1">Coordonnées</label>
                <input
                  type="text"
                  className="workspace-input w-full"
                  value={formData.contactInfo}
                  onChange={(e) => setFormData(prev => ({ ...prev, contactInfo: e.target.value }))}
                  placeholder="Email, téléphone..."
                />
              </div>

              <div className="pt-4 border-t border-workspace flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !formData.name.trim()}
                >
                  {loading ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Ajouter')}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
